import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, MapPin, Phone } from "lucide-react";
import LandingRenderer from "../components/landing/LandingRenderer";
import { publicApi } from "../lib/api";
import { fbqTrack, gtagConversion, gtagEvent, injectCustomCode, loadGtag, loadMetaPixel, loadTikTok, track, ttqTrack } from "../lib/tracking";
import { studio } from "../mock";
import { Skeleton } from "../components/ui/skeleton";

const LandingPage = () => {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [state, setState] = useState("loading"); // loading | ready | missing

  useEffect(() => {
    let cancelled = false;
    setState("loading");
    publicApi
      .landingPage(slug)
      .then((data) => {
        if (cancelled) return;
        setPage(data);
        setState("ready");
        document.title = `${data.title} | ${studio.name}`;
        // Tracking: page view
        track(slug, "view");
        // Ad platform pixels
        const px = data.pixel || {};
        if (px.enabled) {
          if (px.meta_pixel_id) loadMetaPixel(px.meta_pixel_id.trim());
          if (px.custom_code) injectCustomCode(px.custom_code, `avalon-custom-pixel-${data.id}`);
        }
        const gtagIds = [];
        if (px.ga4_enabled && px.ga4_measurement_id) gtagIds.push(px.ga4_measurement_id);
        if (px.google_ads_enabled && px.google_ads_id) gtagIds.push(px.google_ads_id);
        if (gtagIds.length) loadGtag(gtagIds);
        if (px.tiktok_enabled && px.tiktok_pixel_id) loadTikTok(px.tiktok_pixel_id.trim());
      })
      .catch(() => !cancelled && setState("missing"));
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const onCta = (label, url) => {
    track(slug, "click", { cta_label: label, cta_url: url });
    const px = page?.pixel || {};
    if (px.enabled) fbqTrack(px.cta_event || "Lead", { content_name: label, page: slug });
    if (px.ga4_enabled && px.ga4_measurement_id) gtagEvent("cta_click", { cta_label: label, cta_url: url, landing_page: slug });
    if (px.google_ads_enabled && px.google_ads_id) gtagConversion(px.google_ads_id, px.google_ads_conversion_label, { landing_page: slug });
    if (px.tiktok_enabled && px.tiktok_pixel_id) ttqTrack(px.tiktok_cta_event || "ClickButton", { content_name: label, description: slug });
  };

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="landing-page">
      {/* Minimal header */}
      <header className="absolute inset-x-0 top-0 z-40">
        <div className="container-x flex h-[76px] items-center justify-between">
          <Link to="/" className="group flex items-center gap-3" data-testid="lp-logo">
            <span className="flex h-9 w-9 items-center justify-center border border-foreground/30 transition-colors group-hover:border-primary">
              <span className="display text-2xl leading-none">A</span>
            </span>
            <span className="flex flex-col leading-none">
              <span className="display text-2xl tracking-wide">AVALON</span>
              <span className="text-[9px] font-semibold uppercase tracking-[0.32em] text-muted-foreground">Tattoo Collective</span>
            </span>
          </Link>
          <a href={studio.phoneHref} className="hidden items-center gap-2 text-[12px] font-semibold tracking-[0.12em] text-muted-foreground transition-colors hover:text-foreground sm:flex">
            <Phone className="h-3.5 w-3.5" /> {studio.phone}
          </a>
        </div>
      </header>

      {state === "loading" && (
        <div className="container-x pt-40" data-testid="lp-loading">
          <Skeleton className="h-4 w-48" />
          <Skeleton className="mt-6 h-24 w-3/4" />
          <Skeleton className="mt-4 h-24 w-1/2" />
          <Skeleton className="mt-8 h-6 w-2/3" />
        </div>
      )}

      {state === "missing" && (
        <main className="container-x flex min-h-[80vh] flex-col items-start justify-center pt-[76px]" data-testid="lp-missing">
          <p className="eyebrow">Unavailable</p>
          <h1 className="display mt-6 text-[clamp(3rem,10vw,9rem)] uppercase leading-[0.82] tracking-wide">
            This page isn&apos;t <span className="text-stroke">live</span>
          </h1>
          <p className="mt-6 max-w-md text-muted-foreground">The promotion you&apos;re looking for has ended or hasn&apos;t been published yet.</p>
          <Link to="/" className="mt-10 inline-flex h-12 items-center gap-2 bg-primary px-7 text-[11px] font-semibold uppercase tracking-[0.2em] text-primary-foreground">
            <ArrowLeft className="h-4 w-4" /> Back to Avalon
          </Link>
        </main>
      )}

      {state === "ready" && <LandingRenderer page={page} onCta={onCta} />}

      {/* Minimal footer */}
      <footer className="border-t border-border">
        <div className="container-x flex flex-col gap-3 py-8 text-[11px] uppercase tracking-[0.18em] text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
          <p className="flex items-center gap-2">
            <MapPin className="h-3.5 w-3.5 text-primary" /> {studio.address.full}
          </p>
          <p>
            {studio.hours.display} &middot; <a href={studio.phoneHref} className="hover:text-foreground">{studio.phone}</a>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
